/**
 * ProjectsList Component
 *
 * Displays a list of projects with cards showing key information.
 * Supports hover and selection states for map synchronization.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Building2 } from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import { getUrlWithCountry } from '../../utils/countryUtils';
import { Badge } from '../../components/ui';
import { cn } from '../../lib/utils';

function ProjectsList({
    projects,
    hoveredProjectId,
    selectedProjectId,
    onHover,
    onSelect,
    countryConfig
}) {
    const navigate = useNavigate();
    const { country } = useCountry();

    if (!projects || projects.length === 0) {
        return null;
    }

    const handleProjectClick = (project) => {
        const path = getUrlWithCountry(`/proyectos/${project.slug}`, country);
        if (path.includes('?')) {
            window.location.href = path;
        } else {
            navigate(path);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PUBLICADO': {
                label: 'Publicado',
                variant: 'success',
            },
            'EDICION': {
                label: 'En Edición',
                variant: 'warning',
            },
            'ARCHIVADO': {
                label: 'Archivado',
                variant: 'default',
            },
            'BORRADOR': {
                label: 'Borrador',
                variant: 'info',
            }
        };

        return statusConfig[status] || statusConfig['PUBLICADO'];
    };

    return (
        <div className="space-y-3">
            <AnimatePresence mode="popLayout">
                {projects.map((project, index) => {
                    const isHovered = hoveredProjectId === project.id;
                    const isSelected = selectedProjectId === project.id;
                    const isHighlighted = isHovered || isSelected;
                    const statusConfig = getStatusBadge(project.status || 'PUBLICADO');

                    return (
                        <motion.div
                            key={project.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{
                                duration: 0.2,
                                delay: index * 0.03
                            }}
                            layout
                        >
                            <div
                                className={cn(
                                    'group relative overflow-hidden rounded-xl border transition-all duration-200 cursor-pointer',
                                    'bg-white dark:bg-gray-800',
                                    isHighlighted
                                        ? 'border-orange-500 shadow-md ring-1 ring-orange-500/40'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 hover:shadow-sm'
                                )}
                                onMouseEnter={() => onHover && onHover(project.id)}
                                onMouseLeave={() => onHover && onHover(null)}
                                onClick={() => {
                                    if (onSelect) onSelect(project.id);
                                }}
                            >
                                <div className="relative p-4">
                                    {/* Header with Title and Status */}
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <h3 className={cn(
                                            'flex-1 min-w-0 text-[15px] font-semibold text-gray-900 dark:text-white line-clamp-2 leading-snug tracking-tight',
                                            'group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors'
                                        )}>
                                            {project.name}
                                        </h3>

                                        <Badge
                                            variant={statusConfig.variant}
                                            size="sm"
                                            className="flex-shrink-0"
                                        >
                                            {statusConfig.label}
                                        </Badge>
                                    </div>

                                    {/* Location */}
                                    <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-[13px] mb-3">
                                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="line-clamp-1">
                                            {project.location || project.district || project.province || 'Ubicación no especificada'}
                                        </span>
                                    </div>

                                    {/* Description if available */}
                                    {project.description && (
                                        <p className="text-[13px] text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 leading-relaxed">
                                            {project.description}
                                        </p>
                                    )}

                                    {/* Stats Row */}
                                    {(project.total_lots || project.total_area) && (
                                        <div className="flex items-center gap-4 mb-3 text-[13px] text-gray-600 dark:text-gray-400">
                                            {project.total_lots && (
                                                <div className="flex items-center gap-1.5">
                                                    <Building2 className="w-3.5 h-3.5" />
                                                    <span><span className="font-semibold text-gray-900 dark:text-white">{project.total_lots}</span> lotes</span>
                                                </div>
                                            )}
                                            {project.total_area && (
                                                <div className="flex items-center gap-1.5">
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                                    </svg>
                                                    <span><span className="font-semibold text-gray-900 dark:text-white">{project.total_area.toLocaleString('es-ES')}</span> m²</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Footer with CTA */}
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                                        <motion.button
                                            className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold text-[13px] transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleProjectClick(project);
                                            }}
                                            whileHover={{ x: 2 }}
                                        >
                                            <span>Ver proyecto</span>
                                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                        </motion.button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}

export default ProjectsList;
